import { cloneElement, useCallback, useEffect, useState } from "react";
import { Drawer } from "vaul";
import { ObserverEvent } from "~/services/SimpleObservable";
import { bottomSheetService, BottomSheetType } from "./BottomSheetService";
import { styled } from "~/application/theme";

const StyledDrawerContent = styled(Drawer.Content, {
  position: "fixed",
  bottom: "0 !important",
  left: 0,
  right: 0,
  backgroundColor: "white",
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
  boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.1)",
  zIndex: 101,
  display: "flex",
  flexDirection: "column",
  transform: "translate3d(0, 0, 0)",
  willChange: "transform",
  overflow: "visible",
});

export function BottomSheetContainer() {
  const [bottomSheetPack, setBottomSheetPack] = useState<BottomSheetType[]>([]);
  const [visibleSheets, setVisibleSheets] = useState<Set<number>>(new Set());
  const hideBottomSheetById = useCallback(
    (id: number, fromService = false) => {
      setVisibleSheets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });

      if (!fromService) {
        bottomSheetService.popBottomSheet(id);
      }

      setTimeout(() => {
        setBottomSheetPack((old) => old.filter((d) => d.id !== id));
      }, 300);
    },
    [setBottomSheetPack],
  );

  const onUpdate = (change?: ObserverEvent): void => {
    if (change?.type === "hide") {
      hideBottomSheetById(change.data, true);
      return;
    }

    const newBottomSheet = bottomSheetService.getBottomSheet();

    setBottomSheetPack((old) => {
      const exists = old.some((sheet) => sheet.id === newBottomSheet.id);
      if (exists) {
        return old.map((sheet) => (sheet.id === newBottomSheet.id ? { ...newBottomSheet } : sheet));
      }
      return [...old, { ...newBottomSheet }];
    });

    requestAnimationFrame(() => {
      setVisibleSheets((prev) => {
        const newSet = new Set(prev);
        newSet.add(newBottomSheet.id);
        return newSet;
      });
    });
  };

  useEffect(() => {
    bottomSheetService.subscribe(onUpdate);
  }, []);

  return (
    <>
      {bottomSheetPack.map((bottomSheet) => (
        <Drawer.Root
          key={bottomSheet.id + bottomSheet.component.type.name}
          open={visibleSheets.has(bottomSheet.id)}
          onOpenChange={(open) => {
            if (!open) {
              hideBottomSheetById(bottomSheet.id);
            }
          }}
          dismissible={!bottomSheet.config?.noClose}
          shouldScaleBackground={false}
          modal={true}
          repositionInputs={false}
        >
          <Drawer.Portal>
            <Drawer.Overlay
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                zIndex: 100,
              }}
            />
            <StyledDrawerContent
              style={{
                height: bottomSheet.config?.fullScreen
                  ? "100vh"
                  : bottomSheet.config?.height || "50vh",
                maxHeight: bottomSheet.config?.fullScreen
                  ? "100vh"
                  : bottomSheet.config?.height || "50vh",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "8px 0",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "4px",
                    backgroundColor: "#E0E0E0",
                    borderRadius: "2px",
                  }}
                />
              </div>
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  WebkitOverflowScrolling: "touch",
                  minHeight: 0,
                  position: "relative",
                }}
              >
                {cloneElement(bottomSheet.component, {
                  onCloseClick: () => hideBottomSheetById(bottomSheet.id),
                })}
              </div>
            </StyledDrawerContent>
          </Drawer.Portal>
        </Drawer.Root>
      ))}
    </>
  );
}
